import { useEffect } from 'react';
import { UseFormClearErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { TextInput, Typography } from '@vp/swan';
import { format, parseISO } from 'date-fns';
import { __ } from '@99designs/i18n';
import { useBriefFormContext } from '../../../BriefContext/BriefFormContext';
import { FormInput } from '../../BriefForm';
import { DateFieldInputFragment } from './DateField.generated';
import { useUpdateDateField } from './useUpdateDateField';

export type DateFieldProps = DateFieldInputFragment & {
    register: UseFormRegister<FormInput>;
    setValue: UseFormSetValue<FormInput>;
    clearErrors: UseFormClearErrors<FormInput>;
};

export function Input({
    id,
    register,
    dateValue,
    setValue,
    required,
    clearErrors,
    errorMessage,
}: DateFieldProps) {
    const { briefId } = useBriefFormContext();
    const updateMutation = useUpdateDateField(id, briefId, dateValue);

    const errorMsg = errorMessage || __('This field is required');

    useEffect(() => {
        setValue(id, formatDate(dateValue) ?? '');
    }, [dateValue, id, setValue]);

    return (
        <TextInput
            type="date"
            data-testid={id}
            style={{
                WebkitAppearance: 'none',
                appearance: 'none',
            }}
            {...register(id, {
                required: required ? errorMsg : false,
                onBlur: (e) => {
                    setValue(id, e.target.value);
                    updateMutation(e.target.value);
                    clearErrors(id);
                },
            })}
            {...(dateValue ? { defaultValue: formatDate(dateValue) } : {})}
            aria-required={required ? true : undefined}
        />
    );
}

interface DateAreaFieldViewProps {
    dateValue: DateFieldInputFragment['dateValue'];
}

export function View({ dateValue }: DateAreaFieldViewProps) {
    if (!dateValue) {
        return null;
    }

    return <Typography style={{ whiteSpace: 'pre-wrap' }}>{formatViewDate(dateValue)}</Typography>;
}

const DATE_FORMAT = 'yyyy-MM-dd';

function formatDate(date?: string) {
    if (!date) {
        return '';
    }
    const parsedDate = parseISO(date);
    return format(parsedDate, DATE_FORMAT);
}

function formatViewDate(dateString: string) {
    const date = new Date(dateString);
    const userLocale = navigator.language || 'en-US';
    return new Intl.DateTimeFormat(userLocale).format(date);
}
